/**
 * ReviewStep - Step de resumo que mostra toda a arvore antes do processamento.
 */

import { useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Chip, Divider, Alert,
  List, ListItem, ListItemText, ListItemIcon,
} from '@mui/material';
import {
  Person as PersonIcon,
  MedicalServices as ServiceIcon,
  LocationOn as PlaceIcon,
  CalendarMonth as CalendarIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { formatNames } from '../../../utils/wizardContextBuilder';
import { pluralizeWithCount } from '../../../utils/pluralize';

// Helper para formatar shifts em format legivel (dias da semana com horarios)
const formatScheduleDetails = (shifts) => {
  if (!shifts || shifts.length === 0) return 'Sem turnos configurados';

  // Mapa de traducao de dias
  const dayTranslations = {
    monday: 'Seg', tuesday: 'Ter', wednesday: 'Qua', thursday: 'Qui',
    friday: 'Sex', saturday: 'Sab', sunday: 'Dom'
  };

  // Agrupar shifts por horario
  const scheduleMap = {};
  for (const shift of shifts) {
    const key = `${shift.startTime} às ${shift.endTime}`;
    if (!scheduleMap[key]) {
      scheduleMap[key] = [];
    }
    // Extrair os dias do nome do shift (ex: "Seg/Ter/Qua - Manha (08:30-12:00)")
    for (const day of shift.days || []) {
      const dayLabel = dayTranslations[day] || day;
      if (!scheduleMap[key].includes(dayLabel)) {
        scheduleMap[key].push(dayLabel);
      }
    }
  }

  // Formatar como "Seg (08 às 18), Ter (08 às 18), Qua (09 às 17)"
  const formatted = Object.entries(scheduleMap).map(([timeRange, days]) => {
    const [start, end] = timeRange.split(' às ');
    return `${days.join('/')} (${start} às ${end})`;
  });

  return formatted.join(', ');
};

export default function ReviewStep({
  entityTree,
  onValidChange,
}) {
  const { tree } = entityTree;

  useEffect(() => {
    onValidChange(true);
  }, [onValidChange]);

  const selectedProfessionals = useMemo(
    () => tree.professionals.filter(p => p.selected),
    [tree.professionals]
  );
  const selectedServices = useMemo(
    () => tree.services.filter(s => s.selected),
    [tree.services]
  );
  const selectedPlaces = useMemo(
    () => tree.places.filter(p => p.selected),
    [tree.places]
  );

  const newCount = [
    ...selectedProfessionals.filter(p => p.isNew),
    ...selectedServices.filter(s => s.isNew),
    ...selectedPlaces.filter(p => p.isNew),
  ].length;

  // Filtrar apenas vínculos novos (que envolvem pelo menos uma entidade nova)
  const newLinks = useMemo(() => {
    return tree.links.placeServiceProfessionals.filter(link => {
      const prof = tree.professionals.find(p => p.id === link.professional);
      const svc = tree.services.find(s => s.id === link.service);
      const place = tree.places.find(p => p.id === link.place);
      return prof?.isNew || svc?.isNew || place?.isNew;
    });
  }, [tree.links.placeServiceProfessionals, tree.professionals, tree.services, tree.places]);

  // Agrupar vínculos por profissional (e local, se multiplos)
  const groupedLinks = useMemo(() => {
    const groups = {};
    for (const link of newLinks) {
      const prof = tree.professionals.find(p => p.id === link.professional);
      const place = tree.places.find(p => p.id === link.place);
      const groupKey = selectedPlaces.length > 1
        ? `${link.place}_${link.professional}`
        : link.professional;
      if (!groups[groupKey]) {
        groups[groupKey] = { prof, place, services: [] };
      }
      const svc = tree.services.find(s => s.id === link.service);
      if (svc) groups[groupKey].services.push(svc);
    }
    return Object.values(groups);
  }, [newLinks, tree.professionals, tree.services, tree.places, selectedPlaces.length]);

  const linkCount = newLinks.length;
  const newScheduleCount = tree.schedules.filter(s => !s.isExisting && s.shifts?.length > 0).length;
  const existingScheduleCount = tree.schedules.filter(s => s.isExisting && s.existingScheduleId).length;
  const scheduleCount = newScheduleCount + existingScheduleCount;

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
          Resumo da Configuração
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Confira as entidades e vínculos que serão criados. Você pode voltar para ajustar.
        </Typography>
      </Box>

      {/* Profissionais */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="bold">
            Profissionais ({selectedProfessionals.length})
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {selectedProfessionals.map(p => (
            <Chip
              key={p.id}
              label={p.name}
              color={p.isNew ? 'primary' : 'default'}
              variant={p.isNew ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Box>
      </Paper>

      {/* Serviços */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ServiceIcon color="secondary" />
          <Typography variant="subtitle1" fontWeight="bold">
            Serviços ({selectedServices.length})
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {selectedServices.map(s => (
            <Chip
              key={s.id}
              label={s.name}
              color={s.isNew ? 'secondary' : 'default'}
              variant={s.isNew ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Box>
      </Paper>

      {/* Locais */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <PlaceIcon color="success" />
          <Typography variant="subtitle1" fontWeight="bold">
            Locais ({selectedPlaces.length})
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {selectedPlaces.map(p => (
            <Chip
              key={p.id}
              label={p.name}
              color={p.isNew ? 'success' : 'default'}
              variant={p.isNew ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Box>
      </Paper>

      {/* Quem faz o que? */}
      {linkCount > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LinkIcon color="info" />
            <Typography variant="subtitle1" fontWeight="bold">
              Quem faz o que? ({linkCount})
            </Typography>
          </Box>
          <List dense>
            {groupedLinks.map((group, i) => (
              <ListItem key={i} sx={{ py: 0.25 }}>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      <strong>{group.prof?.name}</strong> fará <strong>{formatNames(group.services)}</strong>
                      {selectedPlaces.length > 1 && <> em <strong>{group.place?.name}</strong></>}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Agendas */}
      {scheduleCount > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CalendarIcon color="warning" />
            <Typography variant="subtitle1" fontWeight="bold">
              Agendas ({scheduleCount})
            </Typography>
          </Box>
          <List dense>
            {/* Agendas existentes a atualizar */}
            {tree.schedules.filter(s => s.isExisting && s.existingScheduleId).map((sched, i) => {
              const prof = tree.professionals.find(p => p.id === sched.professional);
              return (
                <ListItem key={`existing_${i}`} sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        <Chip size="small" label="Atualizar" color="info" sx={{ mr: 1 }} />
                        {sched.name || `Agenda de ${prof?.name}`}
                      </Typography>
                    }
                    secondary="Adicionar serviços aos turnos existentes"
                  />
                </ListItem>
              );
            })}
            {/* Novas agendas a criar */}
            {tree.schedules.filter(s => !s.isExisting && s.shifts?.length > 0).map((sched, i) => {
              const prof = tree.professionals.find(p => p.id === sched.professional);
              const place = tree.places.find(p => p.id === sched.place);
              return (
                <ListItem key={`new_${i}`} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        <Chip size="small" label="Criar" color="warning" sx={{ mr: 1 }} />
                        {sched.name || `Agenda de ${prof?.name} em ${place?.name}`}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {formatScheduleDetails(sched.shifts)}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </Paper>
      )}

      {/* Resumo numerico */}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {newCount > 0 && (
          <Chip label={`${pluralizeWithCount(newCount, 'entidade nova', 'entidades novas')} a criar`} color="primary" />
        )}
        {linkCount > 0 && (
          <Chip label={`${pluralizeWithCount(linkCount, 'vínculo', 'vínculos')} a criar`} color="info" />
        )}
        {newScheduleCount > 0 && (
          <Chip label={`${pluralizeWithCount(newScheduleCount, 'agenda')} a criar`} color="warning" />
        )}
        {existingScheduleCount > 0 && (
          <Chip label={`${pluralizeWithCount(existingScheduleCount, 'agenda')} a atualizar`} color="info" />
        )}
      </Box>
    </Box>
  );
}
